import React, { useEffect } from 'react';

import { RouterProvider } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { ErrorBoundary } from '@shared/components';
import { ApiClient } from '@shared/services';
import { configureSentryScope } from '@shared/utils';
import { useScreenMediaQuery } from '@shared/hooks';

import Splash from '@/components/shared/Splash';

import { RootState } from './redux/ducks';
import router from './router/router';
import constants from './utils/constants';
import { dayjs } from './utils/dateTimeUtils';
import { useMerchantPermissions } from './hooks/useMerchantPermissions';
import ToastMessage from './views/component/ToastMessage';
import themeConfig from './theme/themeConfig';

const { BUILD_ENVIRONMENT, BUILD_NUMBER, BRANCH_NAME, BUILD_TIMESTAMP } =
	constants;
const App = () => {
	const userDetails = useSelector((state: RootState) => state.userAuth.user);
	const { currentUserMID } = useMerchantPermissions();
	const toast = useSelector((state: RootState) => state.toast);
	const initializing = useSelector(
		(state: RootState) => state.init.initializing,
	);

	useEffect(() => {
		if (BUILD_ENVIRONMENT !== 'production') {
			console.log(
				'Build Version:',
				BUILD_NUMBER,
				'\n Branch Name:',
				BRANCH_NAME,
				'\nCreated At:',
				dayjs(BUILD_TIMESTAMP).format('DD/MM/YY, hh:mm:ss a'),
			);
		}
		return () => {
			ApiClient.cleanNetworkMetricsObserver();
		};
	}, []);

	useEffect(() => {
		if (!userDetails?.userId) return;

		configureSentryScope({
			tags: {
				merchantId: currentUserMID,
			},
			user: {
				id: userDetails.userId,
				merchantId: currentUserMID,
				username: userDetails.name,
			},
		});
	}, [currentUserMID, userDetails]);

	const {
		mobile,
		tablet,
		desktop,
		ldesktop,
		xldesktop,
		maxMobile,
		maxTablet,
		minDesktop,
	} = themeConfig;

	useScreenMediaQuery.setMediaOptions({
		desktop,
		mobile,
		tablet,
		ldesktop,
		xldesktop,
		maxMobile,
		maxTablet,
		minDesktop,
	});

	return (
		<ErrorBoundary>
			{initializing ? (
				<Splash />
			) : (
				<RouterProvider router={router} fallbackElement={<Splash />} />
			)}
			{toast.isVisible ? <ToastMessage hideToast={() => {}} /> : null}
		</ErrorBoundary>
	);
};

export default App;
